import { useState } from "react";
import {
  CaretRightIcon as CaretRight,
  WarningOctagonIcon as WarningOctagon,
  ArchiveIcon as Archive,
  CircleNotchIcon as Spinner,
} from "@phosphor-icons/react";

/** A risk chip — used inline in the repeated-names table.
 *  Keeps the visual language identical to the per-workspace opportunities page. */
function Chip({ tone, label, testid }) {
  const palette = {
    risk:     { bg: "#fff1f1", text: "#a2191f", border: "#ffd7d9" },
    warning:  { bg: "#fff8e1", text: "#a56600", border: "#f1c21b" },
    info:     { bg: "#edf5ff", text: "#0043ce", border: "#a6c8ff" },
    muted:    { bg: "var(--ml-surface)", text: "var(--ml-text-secondary)", border: "var(--ml-border)" },
  }[tone] || { bg: "var(--ml-surface)", text: "var(--ml-text-secondary)", border: "var(--ml-border)" };
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 ml-mono text-[10px] uppercase tracking-widest border"
      style={{ background: palette.bg, color: palette.text, borderColor: palette.border }}
    >
      {label}
    </span>
  );
}

function chipsForRow(row) {
  const chips = [];
  if (row.unjournaled_count > 0) {
    chips.push({
      tone: "risk",
      testid: `chip-unjournaled-${row.file_name}`,
      label: (
        <span className="inline-flex items-center gap-0.5">
          <WarningOctagon size={9} weight="bold" />
          {row.unjournaled_count}× unjournaled
        </span>
      ),
    });
  }
  if (row.orphan_count > 0) {
    chips.push({
      tone: "warning",
      testid: `chip-orphan-${row.file_name}`,
      label: (
        <span className="inline-flex items-center gap-0.5">
          <Archive size={9} weight="bold" />
          {row.orphan_count}× orphan
        </span>
      ),
    });
  }
  if (row.total_records >= 100000) {
    chips.push({
      tone: "info",
      testid: `chip-large-${row.file_name}`,
      label: `${(row.total_records / 1000).toFixed(0)}k records`,
    });
  } else if (row.total_records > 0) {
    chips.push({
      tone: "muted",
      testid: `chip-records-${row.file_name}`,
      label: `${row.total_records.toLocaleString()} records`,
    });
  }
  return chips;
}

/** Sortable, filterable table of file names appearing in 2+ engagements. */
export default function RepeatedNamesTable({ rows, loading, onPick, selected }) {
  const [filter, setFilter] = useState("");
  const filtered = filter
    ? rows.filter((r) => r.file_name.includes(filter.toUpperCase()))
    : rows;

  return (
    <div className="ml-card p-5" data-testid="repeated-names-card">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
            Recurring file names
          </div>
          <h2 className="text-lg font-semibold mt-0.5">
            Patterns that span engagements
          </h2>
        </div>
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by name…"
          data-testid="repeated-names-filter"
          className="px-3 py-1.5 text-sm border border-[var(--ml-border)] focus:outline-none focus:border-[var(--ml-primary)] w-48"
        />
      </div>

      {loading ? (
        <div
          className="flex items-center gap-2 text-sm text-[var(--ml-text-secondary)] py-8"
          data-testid="repeated-names-loading"
        >
          <Spinner size={14} className="animate-spin" /> Mining across engagements…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-[var(--ml-text-secondary)] py-8" data-testid="repeated-names-empty">
          {rows.length === 0
            ? "No file names repeat across engagements yet. Once you've imported metadata for a second workspace, recurring names will surface here."
            : `No names match "${filter}".`}
        </div>
      ) : (
        <table className="w-full text-sm" data-testid="repeated-names-table">
          <thead>
            <tr className="border-b border-[var(--ml-border)] bg-[var(--ml-surface)]">
              <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">File name</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Engagements</th>
              <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Library</th>
              <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Signals</th>
              <th className="px-2 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr
                key={r.file_name}
                data-testid={`repeated-row-${r.file_name}`}
                role="button"
                tabIndex={0}
                onClick={() => onPick(r.file_name)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onPick(r.file_name);
                  }
                }}
                aria-label={`Drill down into ${r.file_name} (appears in ${r.workspace_count} engagements)`}
                className={`border-b border-[var(--ml-border)] last:border-b-0 hover:bg-[var(--ml-surface)] focus:bg-[var(--ml-surface)] focus:outline-2 focus:outline-[var(--ml-primary)] cursor-pointer transition-colors ${
                  selected === r.file_name ? "bg-[var(--ml-surface)]" : ""
                }`}
              >
                <td className="px-2 py-2 font-mono text-xs font-semibold">{r.file_name}</td>
                <td className="px-2 py-2 text-right tabular-nums font-semibold">{r.workspace_count}</td>
                <td className="px-2 py-2 font-mono text-xs text-[var(--ml-text-secondary)]">
                  {r.libraries}
                </td>
                <td className="px-2 py-2">
                  <div className="flex flex-wrap gap-1">
                    {chipsForRow(r).map((c) => (
                      <Chip key={c.testid} {...c} />
                    ))}
                  </div>
                </td>
                <td className="px-2 py-2 text-right">
                  <CaretRight size={12} weight="bold" className="text-[var(--ml-text-secondary)]" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
